import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, FormSubscribe, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import { GuidelinesField } from "../components";
import { GoalFrameworkSelect } from "./components";

import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { useAiSettingsDefaults } from "../../AIAssistants/hooks";

import { SectionLayout, SettingsFormFooter, SettingsToggle, UnsavedChangesTracker } from "../../Components";

type CoachingGoalsTabProps = {
	setIsUnsafe: () => void;
	setIsSafe: () => void;
};

const MIN_GUIDELINES_LENGTH = 20;

const goalFrameworkOptions = [
	{ value: "generalGuidance", label: "General Guidance" },
	{ value: "smart", label: "SMART" },
	{ value: "custom", label: "Custom" },
];

const GOAL_FRAMEWORK_DESCRIPTION =
	"Select your organization's goal-writing framework. Ask PerformYard evaluates goals against its criteria. Choose General Guidance for broad best practices or Custom to define your own.";

const GOAL_SETTING_GUIDELINES_DESCRIPTION =
	"Give Ask PerformYard context about how your organization writes goals. For example, your internal goal-writing principles, what good looks like at your company, or priorities you want goals to connect to. The more context you provide, the more relevant the coaching will be.";

// Base shape used to parse stored settings into form defaults. The min-length rule below is a
// save-time form constraint, not a data-validity rule, so it must not gate loading existing values.
const goalCoachingBaseSchema = AiSettingsSchema.shape.goalCoachingSettings;

const goalCoachingFormSchema = goalCoachingBaseSchema.superRefine((value, ctx) => {
	if (value.goalFramework === "custom" && value.goalSettingGuidelines.trim().length < MIN_GUIDELINES_LENGTH) {
		ctx.addIssue({
			code: "custom",
			message: `Please provide at least ${MIN_GUIDELINES_LENGTH} characters of guidelines.`,
			path: ["goalSettingGuidelines"],
		});
	}
});

const CoachingGoalsTab = ({ setIsUnsafe, setIsSafe }: CoachingGoalsTabProps) => {
	const formRef = useRef<FormHandle<typeof goalCoachingFormSchema>>(null);

	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	const isGoalCoachingEnabled = data?.goalCoachingEnabled ?? true;

	const handleGoalCoachingToggle = async (val: boolean) => {
		await updateAiSettings({ goalCoachingEnabled: val });
		toast.success(`Goal Quality Coach ${val === true ? "Enabled" : "Disabled"}`);
	};

	const handleSave = async ({
		value,
		resetForm,
	}: {
		value: z.infer<typeof goalCoachingFormSchema>;
		resetForm: () => void;
	}) => {
		try {
			await updateAiSettings({ goalCoachingSettings: value });
			toast.success("Your changes have been saved");
			resetForm();
			setIsSafe();
		} catch {
			toast.error("Unable to save settings");
		}
	};

	const defaultValues = useAiSettingsDefaults({
		schema: goalCoachingBaseSchema,
		data,
		extractDefaults: (allDefaults) => allDefaults.goalCoachingSettings,
		extractData: (data) => data.goalCoachingSettings,
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="coaching-goal-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={goalCoachingFormSchema}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="coaching-goal-settings-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>

			<SectionLayout
				key="goal-coaching-section"
				title="Goal Coaching"
				description="Control whether members of your organization can receive improvement suggestions about the goal they are creating."
			>
				<SettingsToggle
					key="goal-quality-coach-toggle"
					id="enable-goal-quality-coach"
					label="Enable Goal Quality Coach"
					description="Disabling this setting will remove PerformYard's ability to improve user created goals."
					checked={isGoalCoachingEnabled}
					onCheckedChange={handleGoalCoachingToggle}
					dataTestId="goal-quality-coach-toggle"
				/>

				<GoalFrameworkSelect
					id="goalFramework"
					dataTestId="goal-framework"
					label="Goal Framework"
					description={GOAL_FRAMEWORK_DESCRIPTION}
					options={goalFrameworkOptions}
				/>

				<FormSubscribe id="goal-framework-watch" selector={(state) => state.values.goalFramework}>
					{(goalFramework) =>
						goalFramework === "custom" ? (
							<GuidelinesField
								id="goalSettingGuidelines"
								dataTestId="goal-setting-guidelines"
								label="Goal Setting Guidelines"
								description={GOAL_SETTING_GUIDELINES_DESCRIPTION}
							/>
						) : null
					}
				</FormSubscribe>
			</SectionLayout>

			<SettingsFormFooter
				key="coaching-goal-settings-footer"
				formId="coaching-goal-settings-form"
				isPending={isPending}
			/>
		</PYForm>
	);
};

export default CoachingGoalsTab;
